/**
 * Organization Context Provider
 * 
 * REQUIREMENTS SUMMARY: Multi-Gym Awareness for Parents
 * 1. Synchronize 'allOrganizationIds' to localStorage whenever organizations are fetched.
 * 2. This enables the AI Assistant (Chatbot) to perform cross-gym queries for parent users.
 * 3. Maintains persistence across dashboard sessions and mobile webview contexts.
 * 4. Selected organization persists across return visits: localStorage + cookie store the choice;
 *    multi-org users see their last-selected org when they come back (no sessionStorage check).
 */

"use client";
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/utils/supabase/client';
import { useAuth } from './AuthContext';
import { withOrganizationScope } from '@/utils/supabase/organizationScope';

import { createLogger } from '@/utils/logger/devLogger';
import {
  rememberOrganization,
  forgetRememberedOrganization,
} from '@/utils/organizations/rememberedOrganization';

const logger = createLogger('OrganizationContext');

// -----------------------------------------------------------------------------
// Server-friendly org selection persistence
// -----------------------------------------------------------------------------
// The app historically stored current org in localStorage only. That prevents
// Server Components from knowing the org context (hurts performance + SSR).
//
// We mirror the selected organization ID into a cookie so Server Components and
// Route Handlers can read it via `next/headers`.
const ORG_SELECTION_COOKIE = 'currentOrganizationId';

/**
 * Gym membership statuses that show the gym to an ATHLETE login. A Lead
 * ('prospect', #4217) sees the gym; an inactive athlete does not.
 */
export const ATHLETE_LOGIN_GYM_STATUSES: string[] = ['active', 'prospect'];

function setOrgSelectionCookie(organizationId: string) {
  if (typeof window === 'undefined') return;
  document.cookie = `${ORG_SELECTION_COOKIE}=${encodeURIComponent(organizationId)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
}

function clearOrgSelectionCookie() {
  if (typeof window === 'undefined') return;
  document.cookie = `${ORG_SELECTION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

function persistOrganizationSelection(organizationId: string | null) {
  if (!organizationId) {
    clearOrgSelectionCookie();
    return;
  }
  setOrgSelectionCookie(organizationId);
}

/**
 * #3392 billing-only re-entry: gyms where the caller's membership is inactive
 * but they still owe money. The server route (U1) computes qualification;
 * this helper only fetches and maps. Any failure returns [] so active-gym
 * access is NEVER blocked on this fetch.
 */
async function fetchBillingOnlyOrganizations(): Promise<Organization[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/billing/billing-only-organizations', {
      credentials: 'include',
      headers: session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {},
    });
    if (!response.ok) {
      throw new Error(`Billing-only organizations request failed: ${response.status}`);
    }
    const payload = await response.json();
    if (!payload?.success || !Array.isArray(payload.organizations)) return [];
    // The route returns the FULL organizations row (plus the two re-entry
    // figures). Spread it whole: consumers of the Organization shape —
    // SubscriptionGuard's status/is_active gate first among them — treat a
    // missing field as "org inactive" and bounce the caller to the
    // account-paused screen.
    return payload.organizations.map((org: Organization): Organization => ({
      ...org,
      billingOnly: true,
    }));
  } catch (err) {
    logger.warn('🏢 [OrganizationContext] Failed to load billing-only organizations; continuing with active orgs only', err);
    return [];
  }
}

// Organization interface
export interface Organization {
  id: string;
  name: string;
  logo_url?: string | null;
  slug?: string | null;
  website?: string | null;
  contact_email?: string | null;
  phone_number?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  status?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  billing_base_rate?: number;
  setup_progress?: {
    profileCompleted?: boolean;
    locationsConfirmed?: boolean;
    staffInvitedActioned?: boolean;
    teamsDefinedActioned?: boolean;
    athleteOnboardingActioned?: boolean;
    [key: string]: any;
  };
  // Auto Monthly Invoicing Defaults
  auto_invoice_enabled?: boolean;
  auto_invoice_start_policy?: 'current_month' | 'next_month';
  auto_invoice_due_date_mode?: 'fixed_day_of_month' | 'days_after_issue';
  auto_invoice_due_day?: number;
  // #2663 — true means auto_invoice_due_day refers to the FOLLOWING month.
  auto_invoice_due_day_next_month?: boolean;
  // #5694 — bill the month after the gym-local current month.
  auto_invoice_bill_month_ahead?: boolean;
  // Per-type check boxes under the master switch (plan 2026-09-12-001). Read them
  // through resolveOrgAutoInvoiceStreams(currentOrganization), never raw.
  auto_invoice_team_tuition_enabled?: boolean;
  auto_invoice_class_enrollments_enabled?: boolean;
  auto_invoice_custom_charges_enabled?: boolean;
  auto_invoice_due_days_after_issue?: number;
  auto_invoice_send_on_day?: number;
  // Stripe Connect Settings (for useSettingsStatus)
  stripe_connect_account_id?: string | null;
  stripe_connect_status?: string | null;
  stripe_payouts_enabled?: boolean | null;
  stripe_charges_enabled?: boolean | null;
  /** CheerVue team testing: when true, UI shows payout as complete without Connect */
  payout_setup_skipped_for_testing?: boolean | null;
  /** User skipped payout setup during onboarding — can set up later */
  payout_setup_skipped?: boolean | null;
  late_fee_amount?: number | null;
  late_fee_grace_period_days?: number | null;
  non_autopay_admin_fee?: number | null;
  // Auto Check-in Settings (for useSettingsStatus)
  auto_checkin_settings?: any | null;
  // Gym-wide default for the per-event rsvp_enabled flag (pre-fills the
  // "Require RSVP" checkbox at event-create time).
  default_rsvp_enabled?: boolean;
  waitlist_claim_window_hours?: number;
  // Onboarding tracking (for useOnboardingStatus)
  onboarding_completed_at?: string | null;
  // All-Star Setup Guide tracking (for useAllStarSetupStatus)
  allstar_setup_path?: 'mid_season' | 'new_season' | 'programs' | null;
  allstar_setup_progress?: Record<string, boolean>;
  allstar_setup_completed_at?: string | null;
  allstar_setup_dismissed?: boolean;
  // Brand Profile setup completion (for useSettingsStatus)
  brand_profile_saved?: boolean;
  // Time Log: per-gym allowlist of branded payroll CSV formats.
  // Generic CSV is always available and NOT stored here. Branded formats
  // ('quickbooks' | 'gusto' | 'adp' | 'paychex') appear in the Reports /
  // Settings / first-time wizard template dropdowns only when present in
  // this array. See utils/timeLog/filterTemplateOptions.ts.
  enabled_payroll_export_formats?: string[];
  // Demo mode fields
  demo_prompt_shown?: boolean;
  is_demo?: boolean;
  // #3392 billing-only re-entry (client-side synthesized, like demo fields).
  // True only for orgs returned by GET /api/billing/billing-only-organizations:
  // the caller's membership there is inactive but they still owe money. Never
  // stored; recomputed on every org fetch.
  billingOnly?: boolean;
  /** Open-balance total in dollars at a billing-only org (from the route). */
  openBalanceTotal?: number;
  /** ACH-in-flight balance total in dollars at a billing-only org (from the route). */
  processingBalanceTotal?: number;
  /** Count of open invoices at a billing-only org (from the route). */
  openInvoiceCount?: number;
  /** When true, parents see evaluation timelines on the athlete profile (Gym Settings → Evaluations). */
  show_evaluation_progression_to_parents?: boolean;
}

// Organization context interface
interface OrganizationContextType {
  currentOrganization: Organization | null;
  setCurrentOrganization: (organization: Organization | null) => void;
  organizations: Organization[];
  isLoading: boolean;
  error: string | null;
  refreshOrganizations: () => Promise<void>;
  // Enhanced properties for multi-gym support
  isMultiGym: boolean;
  needsOrganizationSelection: boolean;
  lastVisitedOrganizations: Organization[];
  // Organization switching state
  isSwitchingOrganization: boolean;
  setSwitchingOrganization: (switching: boolean) => void;
}

// Create context with default values
const OrganizationContext = createContext<OrganizationContextType>({
  currentOrganization: null,
  setCurrentOrganization: () => {},
  organizations: [],
  isLoading: false,
  error: null,
  refreshOrganizations: async () => {},
  isMultiGym: false,
  needsOrganizationSelection: false,
  lastVisitedOrganizations: [],
  isSwitchingOrganization: false,
  setSwitchingOrganization: () => {},
});

// Create provider component
export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const { user, isLoading: authLoading } = useAuth();

  // Read inside handleSetCurrentOrganization, which is deliberately identity-
  // stable (empty dep array). A ref keeps the stamp current without giving the
  // callback a new identity on every sign-in.
  const userRef = useRef(user);
  userRef.current = user;

  // Function to set the current organization with localStorage + cookie persistence
  const handleSetCurrentOrganization = useCallback((organization: Organization | null) => {
    if (!organization) {
      logger.warn('Attempted to set null organization, clearing current organization');
      setCurrentOrganization(null);
      forgetRememberedOrganization();
      persistOrganizationSelection(null);
      return;
    }

    setCurrentOrganization(organization);
    // Stamped with the signed-in user: the remembered gym outlives a change of
    // user in the same browser, and without the stamp nothing downstream can
    // tell a fresh bootstrap from someone else's leftover (#4571).
    rememberOrganization(organization.id, userRef.current?.id);
    persistOrganizationSelection(organization.id);
    logger.info(`Current organization set to: ${organization.name} (${organization.id})`);
  }, []);

  // Function to fetch organizations for the current user
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      forgetRememberedOrganization();
      persistOrganizationSelection(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      logger.info("🏢 [OrganizationContext] Fetching organizations for user...", { userId: user?.id });
      
      // Get user type to determine which table to query
      const { data: userData, error: userError } = await supabase
        .from('auth_users')
        .select('user_type')
        .eq('id', user.id)
        .single();
      
      if (userError || !userData) {
        logger.warn("User data not found");
        setOrganizations([]);
        setCurrentOrganization(null);
        forgetRememberedOrganization();
        persistOrganizationSelection(null);
        return;
      }
      
      let orgData: any[] | null = null;
      let error: any = null;
      
      if (userData.user_type === 'parent') {
        // Handle parent users - get organizations through parent_organizations
        const { data: parentData, error: parentError } = await supabase
          .from('parents')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();
        
        if (parentError || !parentData) {
          logger.warn("Parent record not found for user");
          setOrganizations([]);
          setCurrentOrganization(null);
          forgetRememberedOrganization();
          return;
        }
        
        // Get organization IDs through parent_organizations  
        const parentOrgResult = await supabase
          .from('parent_organizations')
          .select('organization_id')
          .eq('parent_id', parentData.id)
          .eq('status', 'active');
        
        if (parentOrgResult.error) {
          error = parentOrgResult.error;
          orgData = null;
        } else if (parentOrgResult.data && parentOrgResult.data.length > 0) {
          // Get organization details separately
          const orgIds = parentOrgResult.data.map(item => item.organization_id);
          const orgsResult = await supabase
            .from('organizations')
            .select('*')
            .in('id', orgIds);
          
          orgData = orgsResult.data;
          error = orgsResult.error;
        } else {
          orgData = [];
          error = null;
        }
        
      } else if (userData.user_type === 'gym_staff') {
        // Coach + Parent persona toggle: gym_staff users can also be parents
        // (every coach gets a parents row via the auto_create_parent_row_for_staff
        // trigger). When a coach enrolls their own kid at a gym — whether their
        // home gym or a different one — a parent_organizations row is created
        // there. The org switcher must include those gyms too, otherwise the
        // coach loses access to gyms where their only relationship is being
        // a parent.
        const [staffOrgResult, parentRecordResult] = await Promise.all([
          supabase
            .from('organization_staff')
            .select('organization_id')
            .eq('auth_user_id', user.id)
            .eq('status', 'active'),
          supabase
            .from('parents')
            .select('id')
            .eq('auth_user_id', user.id)
            .maybeSingle(),
        ]);

        if (staffOrgResult.error) {
          error = staffOrgResult.error;
          orgData = null;
        } else {
          const staffOrgIds = (staffOrgResult.data ?? []).map(item => item.organization_id);
          let parentOrgIds: string[] = [];

          if (parentRecordResult.data?.id) {
            const parentOrgResult = await supabase
              .from('parent_organizations')
              .select('organization_id')
              .eq('parent_id', parentRecordResult.data.id)
              .eq('status', 'active');
            if (parentOrgResult.error) {
              logger.warn('🏢 [OrganizationContext] Failed to load parent_organizations for gym_staff user; continuing with staff-only orgs', parentOrgResult.error);
            } else {
              parentOrgIds = (parentOrgResult.data ?? []).map(item => item.organization_id);
            }
          }

          const allOrgIds = Array.from(new Set([...staffOrgIds, ...parentOrgIds]));

          if (allOrgIds.length > 0) {
            const orgsResult = await supabase
              .from('organizations')
              .select('*')
              .in('id', allOrgIds);
            orgData = orgsResult.data;
            error = orgsResult.error;
          } else {
            orgData = [];
            error = null;
          }
        }

      } else if (userData.user_type === 'athlete') {
        // First, get the athlete ID from the auth_user_id
        const athleteResult = await supabase
          .from('athletes')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();
        
        if (athleteResult.error || !athleteResult.data) {
          logger.warn('Athlete not found for user:', user.id);
          orgData = [];
          error = null;
        } else {
          // Get organization IDs through athlete_organizations. A Lead
          // ('prospect', #4217) — e.g. an adult who signed themselves up — is
          // connected to the gym too.
          const athleteOrgResult = await supabase
            .from('athlete_organizations')
            .select('organization_id, status')
            .eq('athlete_id', athleteResult.data.id)
            .in('status', ATHLETE_LOGIN_GYM_STATUSES);
          
          if (athleteOrgResult.error) {
            error = athleteOrgResult.error;
            orgData = null;
          } else if (athleteOrgResult.data && athleteOrgResult.data.length > 0) {
            // Get organization details separately
            const orgIds = athleteOrgResult.data.map(item => item.organization_id);
            const orgsResult = await supabase
              .from('organizations')
              .select('*')
              .in('id', orgIds);
            
            orgData = orgsResult.data;
            error = orgsResult.error;
          } else {
            orgData = [];
            error = null;
          }
        }
        
      } else {
        logger.warn("Unsupported user type:", userData.user_type);
        setOrganizations([]);
        setCurrentOrganization(null);
        forgetRememberedOrganization();
        return;
      }
      
      // Include the shared demo gym if the user holds a dedicated demo-viewer
      // grant (#1354). Demo browsing no longer relies on an organization_staff
      // row, so the demo org won't appear via the staff/parent/athlete queries
      // above — surface it here so the switcher and currentOrganization
      // resolution include it. RLS lets a user read only their own grant and the
      // demo org row, so this is safe under the user's session.
      if (!error) {
        try {
          const { data: demoGrants } = await supabase
            .from('demo_org_viewers')
            .select('demo_org_id')
            .eq('auth_user_id', user.id);
          const demoOrgIds = (demoGrants ?? []).map(g => g.demo_org_id);
          const existingIds = new Set((orgData ?? []).map((o: Organization) => o.id));
          const missingDemoIds = demoOrgIds.filter(id => !existingIds.has(id));
          if (missingDemoIds.length > 0) {
            const { data: demoOrgs } = await supabase
              .from('organizations')
              .select('*')
              .in('id', missingDemoIds);
            if (demoOrgs && demoOrgs.length > 0) {
              orgData = [...(orgData ?? []), ...demoOrgs];
            }
          }
        } catch (demoErr) {
          logger.warn('🏢 [OrganizationContext] Failed to load demo-viewer grant; continuing without demo org', demoErr);
        }
      }

      // #3392 billing-only re-entry: parents (and coaches who are also parents)
      // get back the gyms where their membership is inactive but money is still
      // owed, flagged billingOnly. Awaited HERE, before the empty-check and
      // selection logic below, so (a) a departed parent whose ONLY gym is
      // billing-only still reaches auto-select instead of the no-org redirect,
      // and (b) loading settles once with the full list — no late pop-in.
      // The helper returns [] on any failure, so this never blocks active gyms.
      if (!error && (userData.user_type === 'parent' || userData.user_type === 'gym_staff')) {
        const billingOnlyOrgs = await fetchBillingOnlyOrganizations();
        if (billingOnlyOrgs.length > 0) {
          // Dedupe by id: an org reached through the active path can never
          // also qualify as billing-only, but guard anyway — the active,
          // unflagged version wins.
          const existingIds = new Set((orgData ?? []).map((o: Organization) => o.id));
          const newBillingOnlyOrgs = billingOnlyOrgs.filter(org => !existingIds.has(org.id));
          if (newBillingOnlyOrgs.length > 0) {
            orgData = [...(orgData ?? []), ...newBillingOnlyOrgs];
          }
        }
      }

      logger.info("🏢 [OrganizationContext] Organizations query result:", { orgData, error, dataLength: orgData?.length, userType: userData.user_type });
      
      if (error) {
        logger.error("🏢 [OrganizationContext] Supabase error fetching organizations:", error);
        throw new Error(`Failed to fetch organizations: ${error.message}`);
      }
      
      if (!orgData || orgData.length === 0) {
        logger.warn("No active organizations found for user");
        setOrganizations([]);
        setCurrentOrganization(null);
        forgetRememberedOrganization();
        persistOrganizationSelection(null);
        
        // Allow organization-less users to access specific routes for self-service workflows
        // This supports the "Central Signup + Gym Discovery" flow from the PRD
        const allowedRoutesWithoutOrg = [
          '/signin',
          '/register',
          '/gyms',                    // Public gym catalog
          '/programs',                // Public program catalogs
          '/register-tryout',         // Standalone tryout registration
          '/join/',                   // Gym join link (#4217): a family with no gym yet signs in mid-flow
          '/team-placement-reveal',   // Team placement reveal pages
          '/parent-invitations',      // Gym invite accept page — org-less parents (central signup,
                                      // roster import) must be able to view it to JOIN their first gym
                                      // (2026-06-10: bounce here broke invite accepts for no-org parents)
          '/dashboard',               // User dashboard (add athletes, view profile)
          '/my-registrations',        // View their registrations
          '/profile',                 // Update profile information
          '/athletes',                // Manage athletes (add/edit)
          '/mobile',                  // Mobile WebView pages (security forms, etc.)
          '/setup',                   // Organization setup wizard
          '/pay/',                    // Public POS payment link pages
          '/store/',                  // Public Merch Store pages (#2637)
          '/t/',                      // Event ticket order page (Event Tickets KTD7)
          '/payment/',                // Public payment pages
          '/booking/',                // Public booking pages
          '/enrollment/',             // Public enrollment pages
          '/placement-response/',     // Team placement response pages
          '/terms',                   // Terms of service
          '/privacy',                 // Privacy policy
        ];
        
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        const isAllowedRoute = allowedRoutesWithoutOrg.some(route => currentPath.startsWith(route));
        
        if (!isAllowedRoute && typeof window !== 'undefined') {
          logger.info("Redirecting user without organizations to dashboard page for self-service");
          window.location.href = '/dashboard';
        }
        return;
      }
      
      // Organizations are now returned directly
      const organizations: Organization[] = orgData;
      
      logger.info(`Successfully fetched ${organizations.length} organizations for user`);
      setOrganizations(organizations);

      // Store all organization IDs in localStorage for the AI Assistant (Chatbot)
      // This enables parents with athletes at multiple gyms to perform cross-org queries.
      // #3392: billing-only orgs are EXCLUDED — the assistant's own org gate
      // refuses inactive parents, so aggregating a restricted gym only hands
      // the assistant an error. When every org is billing-only, clear the key
      // so a stale list from a previous session cannot linger.
      if (typeof window !== 'undefined' && organizations.length > 0) {
        const aggregatableOrgIds = organizations
          .filter(org => org.billingOnly !== true)
          .map(org => org.id);
        if (aggregatableOrgIds.length > 0) {
          localStorage.setItem('allOrganizationIds', aggregatableOrgIds.join(','));
          logger.info(`Stored ${aggregatableOrgIds.length} organization IDs in localStorage for AI Assistant`);
        } else {
          localStorage.removeItem('allOrganizationIds');
        }
      }
      
      // Handle organization selection for multi-gym users
      if (organizations.length > 0) {
        const savedOrgId = localStorage.getItem('currentOrganizationId');
        let organizationToSet: Organization | null = null;
        
        if (savedOrgId) {
          // Try to find the saved organization in the fetched data (this validates access)
          organizationToSet = organizations.find((org: Organization) => org.id === savedOrgId) || null;
          if (organizationToSet) {
            logger.info(`Using saved organization: ${organizationToSet.name} (${organizationToSet.id})`);
          } else {
            logger.info(`Saved organization ID ${savedOrgId} not found in fetched organizations - user may have lost access`);
            // Clear invalid saved organization
            forgetRememberedOrganization();
          }
        }
        
        // Organization selection logic
        if (organizations.length === 1) {
          // Single organization - auto-select
          organizationToSet = organizations[0];
          logger.info(`Single organization found, auto-selecting: ${organizationToSet.name} (${organizationToSet.id})`);
          handleSetCurrentOrganization(organizationToSet);
        } else if (organizations.length > 1) {
          // Multi-org user - persist selected org across sessions (return visits)
          // If user has a valid saved org in localStorage, restore it (no sessionStorage check)
          if (organizationToSet) {
            logger.info(`Multi-organization user - restoring saved organization: ${organizationToSet.name} (${organizationToSet.id})`);
            handleSetCurrentOrganization(organizationToSet);
          } else {
            // No saved org or saved org invalid (user lost access) - show selection modal
            logger.info(`Multi-organization user - no valid saved org, showing selection modal`);
            forgetRememberedOrganization();
            persistOrganizationSelection(null);
            setCurrentOrganization(null);
          }
        }
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      logger.error('Error fetching organizations:', errorMessage);
      setError(`Failed to fetch organizations: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, handleSetCurrentOrganization]);

  // Fetch organizations when the user ID changes, not the whole user object.
  // This prevents re-fetching when the user object is refreshed on tab focus.
  useEffect(() => {
    logger.info("🏢 [OrganizationContext] useEffect triggered", { 
      authLoading, 
      hasUser: !!user, 
      userId: user?.id,
      userEmail: user?.email,
      fullUser: user
    });
    
    if (!authLoading) {
      if (user) {
        logger.info("🏢 [OrganizationContext] User found, calling fetchOrganizations");
        fetchOrganizations();
      } else {
        logger.info("🏢 [OrganizationContext] No user, clearing organizations and session data");
        // Clear organizations if user is not logged in
        setOrganizations([]);
        setCurrentOrganization(null);
        forgetRememberedOrganization();
        persistOrganizationSelection(null);
        // Clear session flag so next login shows modal for multi-org users
        sessionStorage.removeItem('hasCompletedInitialOrgSelection');
        setIsLoading(false);
      }
    } else {
      logger.info("🏢 [OrganizationContext] Auth still loading, waiting...");
    }
  }, [user?.id, authLoading]);

  // Realtime listener: update currentOrganization when is_active or status changes.
  // This ensures SubscriptionGuard and SubscriptionWarningBanner react immediately
  // (e.g. when a trial expires or the cron job locks the org while the user is online).
  useEffect(() => {
    if (!currentOrganization?.id) return;

    const channel = supabase
      .channel(`org-status-${currentOrganization.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'organizations',
          filter: `id=eq.${currentOrganization.id}`,
        },
        (payload) => {
          const updated = payload.new as any;
          const visibilityChanged =
            updated.show_evaluation_progression_to_parents !== undefined &&
            Boolean(updated.show_evaluation_progression_to_parents) !==
              Boolean(currentOrganization.show_evaluation_progression_to_parents);
          if (
            updated.is_active !== currentOrganization.is_active ||
            updated.status !== currentOrganization.status ||
            visibilityChanged
          ) {
            logger.info('🏢 [OrganizationContext] Realtime org update detected', {
              orgId: currentOrganization.id,
              visibilityChanged,
            });
            setCurrentOrganization((prev) =>
              prev
                ? {
                    ...prev,
                    is_active: updated.is_active,
                    status: updated.status,
                    ...(updated.show_evaluation_progression_to_parents !== undefined
                      ? {
                          show_evaluation_progression_to_parents: Boolean(
                            updated.show_evaluation_progression_to_parents
                          ),
                        }
                      : {}),
                  }
                : prev
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentOrganization?.id]);
  
  // Function to refresh organizations
  const refreshOrganizations = useCallback(async () => {
    await fetchOrganizations();
  }, [fetchOrganizations]);

  // Multi-gym detection
  const isMultiGym = organizations.length > 1;
  
  // Organization selection needed for sign-in flow  
  // Show modal if: multi-org user AND (no org selected OR current org has permission errors)
  const hasPermissionError: boolean = Boolean(error && String(error).includes('not authorized'));
  const needsOrganizationSelection = isMultiGym && (!currentOrganization || hasPermissionError) && !isLoading;
  
  // Last visited organizations (simple implementation - could be enhanced with localStorage)
  const lastVisitedOrganizations: Organization[] = [];

  // Memoize context value to prevent unnecessary re-renders on token refresh
  const value = useMemo(() => ({
    currentOrganization,
    setCurrentOrganization: handleSetCurrentOrganization,
    organizations,
    isLoading,
    error,
    refreshOrganizations,
    isMultiGym,
    needsOrganizationSelection,
    lastVisitedOrganizations,
    isSwitchingOrganization,
    setSwitchingOrganization: setIsSwitchingOrganization,
  }), [
    currentOrganization,
    handleSetCurrentOrganization,
    organizations,
    isLoading,
    error,
    refreshOrganizations,
    isMultiGym,
    needsOrganizationSelection,
    isSwitchingOrganization,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

// Custom hook to use organization context
export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
} 